/**
 * Shared handling of the DOM copies a continuous crawl ticker needs.
 *
 * The crawl fills the mask by cloning the text list several times over. Every cycle
 * (`intro` / `repeat` / `outro`) throws the previous copies away and makes new ones, so this
 * runs for as long as the graphic plays — it is the one place in the editor whose DOM can
 * grow without bound.
 */

const COPY_CLASS = "item-container-copy"

/**
 * Widths below this are treated as "not measured yet" rather than as a real value. Fonts may
 * still be loading, and `Math.round(width / 0) + 1` is `Infinity`, which used to turn the
 * clone loop into an endless one and hang the tab.
 */
const MIN_MEASURABLE_WIDTH = 1

/**
 * Last-resort ceiling, not a design limit. A short text in a wide ticker legitimately needs
 * dozens of copies to fill the mask, so this must stay well above any realistic
 * parentWidth / textsWidth ratio — capping too low leaves a visible gap scrolling through.
 */
const MAX_COPIES = 1000

const escapeClass = (value: string): string =>
    typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(value) : value

/**
 * How many copies are needed to cover the parent. Returns 0 when the text has not been
 * measured yet — one cycle without copies is recoverable, an infinite loop is not.
 */
export const tickerCopyCount = (parentObjectWidth: number, textsWidth: number): number => {
    if (!Number.isFinite(textsWidth) || textsWidth < MIN_MEASURABLE_WIDTH) return 0
    if (!Number.isFinite(parentObjectWidth) || parentObjectWidth < 0) return 0

    return Math.min(Math.round(parentObjectWidth / textsWidth) + 1, MAX_COPIES)
}

/**
 * Drop every copy belonging to this ticker.
 *
 * `element.remove()` rather than `parent.removeChild(element)`: the query spans the whole
 * document, so a copy could easily sit under a different parent than the one the caller
 * happens to hold — React may have replaced the container in between. `removeChild` then
 * threw `NotFoundError` out of a GSAP `onComplete`, which aborted the rest of the cycle: the
 * old copies stayed in the DOM for good and the ticker chain stopped rescheduling itself.
 */
export const removeTickerCopies = (objectId: string): void => {
    document
        .querySelectorAll(`.${COPY_CLASS}.${escapeClass(objectId)}`)
        .forEach((element) => element.remove())
}

/**
 * Clone the text list and append it as copy `index`.
 *
 * NOTE: the clone deliberately keeps the ids of its descendants. They are duplicates of the
 * originals, which is not clean, but `updateTickerCopiesStyles` finds the copies precisely by
 * `.item-container-copy [id="..."]`.
 */
export const appendTickerCopy = (
    parent: ParentNode,
    textListNode: HTMLElement,
    objectId: string,
    index: number
): HTMLElement => {
    const clone = textListNode.cloneNode(true) as HTMLElement
    clone.id = `${objectId}-copy-${index}`
    clone.className = `${COPY_CLASS} ${textListNode.id} copy-${index}`
    parent.appendChild(clone)
    return clone
}
